import { useEffect, useMemo, useState } from 'react';
import { fetchDedupe } from 'fetch-dedupe';
import useOrganizationSettings from '../../../shared/useOrganizationSettings';
import type { PortalSettings } from '../../../../../../shared/settings/portal';
import { organizationPortalSettingsNotFoundErrorMessage } from '../../../../../../shared/settings/portal';
import { post } from '../../../../../redux/api';
import { useAddToast } from '../../../../../contexts/ToastContext';
import { useAsyncValue } from '../../../../../hooks';
import { errorMessagesListFromResponse, type FailedFetchResponse } from '../../../../../helpers/errors';
import { isSlug } from '../../../../../../shared/utils';
import { usePortalUrl as usePortalUrlContext } from '../../../../shared/publicPortal/hooks';

export const defaultLogoPath = '/img/fossa-primary-logo.svg';

/**
 * Hook for managing portal URL. This hook ensure the portal URL is always up-to-date before and after editing the portal settings.
 */
const usePortalUrl = ({
  slug,
  isDirty,
}: {
  slug?: string;
  isDirty: boolean;
}) => {
  const { url, setSlug } = usePortalUrlContext();
  const [previousSlug, setPreviousSlug] = useState<string | undefined>(slug);
  useEffect(() => {
    // If the slug changed and the settings are not dirty, that means the user successfully saved the settings.
    // The new slug should be used as the portal URL.
    const newSlugSaved = slug && slug !== previousSlug && !isDirty;
    if (newSlugSaved) {
      setPreviousSlug(slug);
      setSlug(slug);
    }
  }, [slug, isDirty, setSlug, previousSlug]);

  return { portalUrl: url };
};

const getLogoEndpoint = (organizationId: number) => `/api/organizations/${organizationId}/settings/portal/logo`;

/**
 * Uploads the logo file to the backend.
 * 1. Get the signed URL from the backend.
 * 2. Upload the file to the signed URL.
 */
async function uploadLogo(logoEndpoint: string, file: File): Promise<void> {
  const response = await post<{ fileName: string; contentType: string }, { signedUrl: string }>(logoEndpoint, {
    fileName: file.name,
    contentType: file.type,
  });
  if (!response?.data?.signedUrl) {
    throw new Error('Failed to upload logo');
  }

  // The put util function doesn't support file uploads.
  // So we use fetch to upload the file to the signed URL similar to the report logo upload.
  await fetchDedupe(response.data.signedUrl, {
    method: 'PUT',
    body: file,
  });
}

/**
 * Hook for managing portal settings
 */
const usePortalSettings = (organizationId: number) => {
  const addToast = useAddToast();
  const [file, setFile] = useState<File | null>(null);
  const fileUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  const {
    settings,
    onUpdateSettings,
    isSaveButtonDisabled,
    isLoading,
    errors,
    initialSettings,
    onSaveSettingsWithOverride,
    isDirty,
  } = useOrganizationSettings<PortalSettings>({
    endpoint: `/api/organizations/${organizationId}/settings/portal`,
    settingsSection: 'portal',
  });
  const { portalUrl } = usePortalUrl({ slug: settings?.slug, isDirty });
  const logoEndpoint = useMemo(() => getLogoEndpoint(organizationId), [organizationId]);
  const uploadLogoAsyncValue = useAsyncValue();
  const [hasLogoDisplayError, setHasLogoDisplayError] = useState(false);

  const logoUrl = settings?.logo && !file ? logoEndpoint : defaultLogoPath;
  const onSave = async () => {
    if (hasLogoDisplayError) {
      addToast({
        type: 'error',
        content: 'Logo cannot be saved.',
      });
      return;
    }
    if (!settings?.title?.trim()) {
      addToast({
        type: 'error',
        content: `Portal name can't be empty`,
      });
      return;
    }
    if (!settings?.description?.trim()) {
      addToast({
        type: 'error',
        content: `Description can't be empty`,
      });
      return;
    }
    if (!isSlug(settings?.slug || '')) {
      addToast({
        type: 'error',
        content: 'Invalid slug',
      });
      return;
    }
    if (file) {
      try {
        uploadLogoAsyncValue.setPending();
        await uploadLogo(logoEndpoint, file);
        uploadLogoAsyncValue.setResolve(null);
      } catch (error) {
        uploadLogoAsyncValue.setReject(
          errorMessagesListFromResponse(error as FailedFetchResponse).map((e) => e.toString()),
        );
        return;
      }
    }

    await onSaveSettingsWithOverride({
      ...settings,
      // The backend expects the logo to be null if the file is null.
      logo: settings?.logo || null,
    } as PortalSettings);
  };

  const onUpdateFile = (uploadFile: File) => {
    setFile(uploadFile);
    onUpdateSettings({
      logo: uploadFile ? uploadFile.name : null,
    });
    setHasLogoDisplayError(false);
  };

  const unsetFile = () => {
    setFile(null);
    onUpdateSettings({
      logo: null,
    });
    setHasLogoDisplayError(false);
  };

  const onImgError = () => {
    setHasLogoDisplayError(true);
  };

  // Remove the organization portal settings not found error from the list of errors because
  // it is not relevant to the user.
  const validOrganizationPortalSettingErrors = useMemo(() => {
    return errors?.filter((error: string) => error !== organizationPortalSettingsNotFoundErrorMessage);
  }, [errors]);
  const allErrors = [...(validOrganizationPortalSettingErrors || []), ...(uploadLogoAsyncValue.errors || [])];

  // Default value for new Portal settings
  if (!settings) {
    onUpdateSettings({
      isEnabled: true,
    });
  }

  return {
    // Use the uploaded file URL if it exists, else use the logo URL from the backend.
    fileUrl: fileUrl ?? logoUrl,
    setFile: onUpdateFile,
    unsetFile,
    handleImgError: onImgError,
    onSave,
    portalUrl,
    settings,
    onUpdateSettings,
    isSaveButtonDisabled,
    isLoading,
    errors: allErrors,
    initialSettings,
  };
};

export default usePortalSettings;
